import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import SEO from '../components/SEO';

const CategoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [allVideos, setAllVideos] = useState([]);
  const [displayedVideos, setDisplayedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [videosToShow, setVideosToShow] = useState(20);
  const observerTarget = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchCategoryVideos = async () => {
      try {
        setLoading(true);
        console.log("Fetching category videos with ID:", id);

        // Fetch category name
        const categoryNameResponse = await fetch(`${API_BASE_URL}/categoryname/${id}`);
        if (categoryNameResponse.ok) {
          const categoryNameResult = await categoryNameResponse.json();
          console.log("Category name response:", categoryNameResult);
          if (categoryNameResult.data && categoryNameResult.data.name) {
            setCategoryName(categoryNameResult.data.name);
          }
        }

        // Fetch category videos
        const response = await fetch(`${API_BASE_URL}/category/${id}/1000`);
        if (!response.ok) {
          throw new Error('Failed to fetch category videos');
        }
        const result = await response.json();
        console.log("Category API response:", result);

        if (result.error || !result.data) {
          throw new Error('Invalid data structure from API');
        }

        // Map videos
        const mappedVideos = result.data.map(video => ({
          id: video.id,
          title: video.video_title,
          poster: `https:${video.yt_thumb}`,
          views: video.site_views,
          duration: video.yt_length
        }));

        setAllVideos(mappedVideos);
        setDisplayedVideos(mappedVideos.slice(0, 20));
        setVideosToShow(20);
      } catch (err) {
        console.error("Error fetching category videos:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryVideos();
  }, [id]);

  // Lazy loading observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && videosToShow < allVideos.length && !loadingMore) {
          setLoadingMore(true);
          setTimeout(() => {
            const newCount = Math.min(videosToShow + 20, allVideos.length);
            setVideosToShow(newCount);
            setDisplayedVideos(allVideos.slice(0, newCount));
            setLoadingMore(false);
          }, 300);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [videosToShow, allVideos, loadingMore]);

  const handleVideoClick = (videoId) => {
    navigate(`/watch/${videoId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-brand-dark text-white">
        <div className="text-center">
          <div className="text-xl mb-4">Loading videos...</div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-brand-dark text-white">
        <div className="text-center">
          <div className="text-xl mb-4">Error loading videos</div>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-gradient-accent text-white rounded-md hover:opacity-90 transition-opacity"
          >
            Go Back Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-brand-dark min-h-screen text-white">
      <SEO
        title={`${categoryName} - Christian Videos`}
        description={`Watch videos from ${categoryName} on Christian Videos.`}
        url={`https://christianvideos.in/categorydetail/${id}`}
      />
      {/* Back Button */}
      <div className="p-4 sm:p-6 flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 hover:text-gray-300 transition-colors text-sm sm:text-base"
        >
          <FiArrowLeft size={20} className="sm:w-6 sm:h-6" />
          <span>Back</span>
        </button>
      </div>

      {/* Category Title */}
      <div className="px-4 sm:px-8 md:px-12 lg:px-20 pb-6 sm:pb-8">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">{categoryName}</h1>
        <p className="text-xs sm:text-sm text-gray-400">{allVideos.length} Videos</p>
      </div>

      {/* Videos Grid */}
      <div className="px-4 sm:px-8 md:px-12 lg:px-20 pb-20">
        {allVideos.length === 0 ? (
          <div className="text-center text-gray-400 py-12">
            No videos found in this category
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 md:gap-6">
              {displayedVideos.map((video) => (
                <div
                  key={video.id}
                  onClick={() => handleVideoClick(video.id)}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg bg-gray-800 aspect-video mb-2 sm:mb-3">
                    <img
                      src={video.poster}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <svg className="w-5 sm:w-6 h-5 sm:h-6 text-brand-dark ml-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                  <h3 className="font-semibold text-xs sm:text-sm line-clamp-2 group-hover:text-blue-400 transition-colors">
                    {video.title}
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">
                    {video.views?.toLocaleString() || 0} Views
                  </p>
                </div>
              ))}
            </div>

            {/* Lazy Loading Indicator */}
            {videosToShow < allVideos.length && (
              <div ref={observerTarget} className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gradient-accent"></div>
              </div>
            )}

            {/* End of Results */}
            {videosToShow >= allVideos.length && allVideos.length > 0 && (
              <div className="text-center py-8">
                <p className="text-sm text-gray-400">No more videos to load</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CategoryDetail;
